import type { ButtonDefWithValue, ButtonType, GamepadButtonAction } from '../../models';
import { DEFAULT_MODIFIER_THRESHOLD, MODIFIER_RELEASE_FRACTION } from '../../models';

export const collectModifierTypes = (actions: GamepadButtonAction[]): Set<ButtonType> => {
  const types = new Set<ButtonType>();

  actions?.forEach((action) => {
    action.modifiers?.forEach((modifier) => {
      // Falsy is ButtonType.UNKNOWN, which can never be held; findUnreachableModifierActions reports it.
      if (modifier?.type) {
        types.add(modifier.type);
      }
    });
  });

  return types;
};

/** Two thresholds, so a trigger resting on one value cannot flap the layer at frame rate. */
export const resolveActiveModifiers = (
  values: ButtonDefWithValue[],
  modifierTypes: ReadonlySet<ButtonType>,
  thresholds: { [buttonType: number]: number },
  previouslyActive: ReadonlySet<ButtonType>,
): Set<ButtonType> => {
  const active = new Set<ButtonType>();

  modifierTypes.forEach((type) => {
    const reading = values?.find((value) => value.type === type);
    const magnitude = Math.abs(reading?.value ?? 0);

    // Not `??`: a stored 0 would pass, and `magnitude >= 0` latches the layer on with no reading at all.
    const configured = thresholds?.[type];
    const engageAt = configured > 0 && configured <= 1 ? configured : DEFAULT_MODIFIER_THRESHOLD;
    const releaseAt = engageAt * MODIFIER_RELEASE_FRACTION;

    const stillHeld = previouslyActive.has(type) && magnitude >= releaseAt;
    const newlyHeld = magnitude >= engageAt;

    if (stillHeld || newlyHeld) {
      active.add(type);
    }
  });

  return active;
};

const modifierCount = (action: GamepadButtonAction): number => action.modifiers?.length ?? 0;

/** A subset test, so no modifiers means eligible whatever is held. That is what keeps old configs intact. */
export const isActionEligible = (action: GamepadButtonAction, activeModifiers: ReadonlySet<ButtonType>): boolean =>
  (action.modifiers ?? []).every((modifier) => activeModifiers.has(modifier.type));

/** Most specific wins, so a button carrying a base and a modified binding switches instead of firing both. */
export const selectEligibleActions = (
  actions: GamepadButtonAction[],
  buttonType: ButtonType,
  activeModifiers: ReadonlySet<ButtonType>,
): GamepadButtonAction[] => {
  const eligible = (actions ?? []).filter(
    (action) => action.source?.type === buttonType && isActionEligible(action, activeModifiers),
  );

  if (eligible.length === 0) {
    return [];
  }

  const mostSpecific = Math.max(...eligible.map((action) => modifierCount(action)));

  return eligible.filter((action) => modifierCount(action) === mostSpecific);
};

/**
 * Not `isActionEligible`: a base action stays eligible whatever is held, but is deselected once a more
 * specific binding takes its input. Stopping must key off this, or a layer change leaves the base
 * binding's channel silent at its last non-zero value instead of zeroed.
 */
export const selectActiveActions = (
  actions: GamepadButtonAction[],
  activeModifiers: ReadonlySet<ButtonType>,
): Set<GamepadButtonAction> => {
  const selected = new Set<GamepadButtonAction>();
  const inputs = new Set((actions ?? []).map((action) => action.source?.type));

  inputs.forEach((type) => {
    selectEligibleActions(actions, type, activeModifiers).forEach((action) => selected.add(action));
  });

  return selected;
};

/**
 * Reported, deliberately not promoted to the base layer: an action authored to need a modifier, firing
 * on a bare press instead, is the dangerous reading of a broken config.
 */
export const findUnreachableModifierActions = (
  actions: GamepadButtonAction[],
  availableTypes: ReadonlySet<ButtonType>,
): { action: GamepadButtonAction; missing: ButtonType[] }[] => {
  const unreachable: { action: GamepadButtonAction; missing: ButtonType[] }[] = [];

  actions?.forEach((action) => {
    const missing = (action.modifiers ?? [])
      .map((modifier) => modifier.type)
      .filter((type) => !availableTypes.has(type));

    if (missing.length > 0) {
      unreachable.push({ action, missing });
    }
  });

  return unreachable;
};

/** An advisory, not an error: both fire, as two base actions on one button always did. */
export const findDuplicateBindings = (actions: GamepadButtonAction[]): GamepadButtonAction[] => {
  const seen = new Set<string>();
  const duplicates: GamepadButtonAction[] = [];

  actions?.forEach((action) => {
    const modifierKey = (action.modifiers ?? [])
      .map((modifier) => modifier.type)
      .sort((left, right) => left - right)
      .join(',');
    const key = `${action.source?.type}|${modifierKey}`;

    if (seen.has(key)) {
      duplicates.push(action);
      return;
    }

    seen.add(key);
  });

  return duplicates;
};

/** Also an advisory: the input really is held, and suppressing it would silently break a visible binding. */
export const findModifierSourceOverlaps = (actions: GamepadButtonAction[]): GamepadButtonAction[] => {
  const modifierTypes = collectModifierTypes(actions);

  return (actions ?? []).filter((action) => modifierCount(action) === 0 && modifierTypes.has(action.source?.type));
};
